import type { CurriculumItemType, Difficulty } from './filter-engine'

export type Format = 'problem' | 'flashcards' | 'exercise'

export interface DecayCandidate {
  id: string
  title: string
  format: Format
  difficulty: Difficulty | '—'
  est: number
  doneCount: number
  lastDone?: string
  subtopic?: string
}

export interface DecayRow {
  id: string
  type: CurriculumItemType
  topic: string
  subtopic: string
  lastSeenWeeks: number
  risk: number
  candidates: DecayCandidate[]
}

export interface DecayGroup {
  key: string
  name: string
  type: CurriculumItemType
  risk: number
  lastSeenWeeks: number
  candidates: DecayCandidate[]
}

const cloneCandidate = (
  candidate: DecayCandidate,
  subtopic?: string,
): DecayCandidate => {
  const clone = { ...candidate }
  if (subtopic === undefined) delete clone.subtopic
  else clone.subtopic = subtopic
  return clone
}

export function groupDecay(
  rows: DecayRow[],
  by: 'topic' | 'subtopic',
  formats: Format[],
): DecayGroup[] {
  const groups = new Map<string, DecayGroup>()

  for (const row of rows) {
    const key =
      by === 'topic'
        ? `${row.type}:${row.topic}`
        : `${row.type}:${row.topic}/${row.subtopic}`
    const name = by === 'topic' ? row.topic : `${row.topic} / ${row.subtopic}`
    const matchingCandidates = row.candidates
      .filter(
        (candidate) =>
          formats.length === 0 || formats.includes(candidate.format),
      )
      .map((candidate) =>
        cloneCandidate(candidate, by === 'topic' ? row.subtopic : undefined),
      )

    const existing = groups.get(key)
    if (existing) {
      existing.risk = Math.max(existing.risk, row.risk)
      existing.lastSeenWeeks = Math.min(
        existing.lastSeenWeeks,
        row.lastSeenWeeks,
      )
      existing.candidates.push(...matchingCandidates)
      continue
    }

    groups.set(key, {
      key,
      name,
      type: row.type,
      risk: row.risk,
      lastSeenWeeks: row.lastSeenWeeks,
      candidates: matchingCandidates,
    })
  }

  return [...groups.values()]
    .filter((group) => group.candidates.length > 0)
    .sort((left, right) => {
      if (left.risk !== right.risk) return right.risk - left.risk
      if (left.name < right.name) return -1
      if (left.name > right.name) return 1
      return 0
    })
}
